import { Captions, CaptionsOff, Clock, Video, VideoOff } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { RacerCarousel } from "./RacerCarousel";
import { Button } from "./ui/button";
import { GameObject } from "@needle-tools/engine";
import { CameraManager } from "../CameraManager";
import { useEffect, useState } from "react";
import { UIManager } from "../UIManager";
import { useTranslation } from "../../i18n/useTranslation";
import { FullScreenButton } from "./FullScreenButton";
import { formatStartTime } from "../../utils/helper-funcs";
import { useEventData } from "./DataProvider";

interface ControlPanelProps {
  showControls: boolean;
  isRunning: boolean;
  showCommentary: boolean;
  onCommentaryClick: () => void;
  currentHorse: string;
  setCurrentHorse: (horse: string) => void;
}

export function ControlPanel({
  isRunning,
  showControls,
  showCommentary,
  onCommentaryClick,
  currentHorse,
  setCurrentHorse,
}: ControlPanelProps) {
  const { t } = useTranslation();
  const [cameraManager, setCameraManager] = useState<CameraManager | null>(null);
  const [uiManager, setUIManager] = useState<UIManager | null>(null);
  const { eventData } = useEventData();

  const handleToggleBroadcast = () => {
    setCurrentHorse(t("control-panel.cycle-horses"));
    cameraManager?.activateBroadcastCamera();
  };

  const handleNextRacer = () => {
    cameraManager?.changeCameraTarget(true);
    const racer = uiManager?.returnHorseInFocusString();
    if (racer) {
      setCurrentHorse(racer);
    }
  };
  
  const handlePreviousRacer = () => {
    cameraManager?.changeCameraTarget(false);
    const racer = uiManager?.returnHorseInFocusString();
    if (racer) {
      setCurrentHorse(racer);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (!cameraManager) {
        const camMan = GameObject.findObjectOfType(CameraManager);
        const uiMan = GameObject.findObjectOfType(UIManager);
        setUIManager(uiMan);
        setCameraManager(camMan);
      } else {
        clearInterval(interval);
      }
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="relative z-50 flex items-center justify-between flex-1 px-3 sm:px-4 md:px-8 py-1 md:py-2 bg-background md:h-14">
      <div className="flex items-center">
        <Clock className="h-4 w-4 xs:h-5 xs:w-5 sm:h-5 sm:w-5 md:h-6 md:w-6 mr-1" />
        <span className="text-xs whitespace-nowrap">
          {formatStartTime(eventData.startTime)}
        </span>
      </div>
      
      {showControls ? (
        <div className="flex items-center justify-center gap-3">
          <Button
            disabled={!isRunning}
            variant="ghost"
            size="sm"
            className="auto-cam hover:bg-inherit disabled:cursor-not-allowed disabled:text-muted-foreground p-1 xs:p-2"
            onClick={handleToggleBroadcast}
          >
                            <Video className="h-4 w-4 xs:h-5 xs:w-5 sm:h-5 sm:w-5 md:h-6 md:w-6" />
          </Button>

          <RacerCarousel
            disableControls={!isRunning}
            onFocusNextRacer={handleNextRacer}
            onFocusPrevRacer={handlePreviousRacer}
            currentHorse={currentHorse}
          />
          
          <Button
            disabled={!isRunning}
            variant="ghost"
            size="sm"
            className="auto-cam hover:bg-inherit disabled:cursor-not-allowed disabled:text-muted-foreground p-1 xs:p-2"
            onClick={onCommentaryClick}
          >
            {showCommentary ? (
              <CaptionsOff className="h-4 w-4 xs:h-5 xs:w-5 sm:h-5 sm:w-5 md:h-6 md:w-6" />
            ) : (
              <Captions className="h-4 w-4 xs:h-5 xs:w-5 sm:h-5 sm:w-5 md:h-6 md:w-6" />
            )}
          </Button>
        </div>
      ) : null}

      <div className="flex items-center">
        <FullScreenButton />
      </div>
    </div>
  );
}